import { createContext, useEffect, useReducer } from 'react';
import JWTDecode from 'jwt-decode';
import PropTypes from 'prop-types';
// utils
import axiosInstance from '../utils/api';
import {
  setSession,
  getRole,
  checkIsKrismar,
  checkIsColombia,
  checkIsColombiaSierra,
  checkIsImex,
  checkIsPreschoolFFE,
  checkIsPreschool
} from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  role: null,
  isKrismar: null,
  isColombiaUser: null,
  isColombiaSierraUser: null,
  isImex: null,
  personLevel: null,
  personGrade: null,
  personLevelId: null,
  personGradeId: null,
  isPreschoolFFE: null,
  isPreschool: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const {
      isAuthenticated,
      user,
      role,
      isKrismar,
      isColombiaUser,
      isColombiaSierraUser,
      isImex,
      personLevel,
      personGrade,
      personLevelId,
      personGradeId,
      isPreschoolFFE,
      isPreschool
    } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      personLevel,
      personGrade,
      personLevelId,
      personGradeId,
      role,
      isKrismar,
      isColombiaUser,
      isColombiaSierraUser,
      isImex,
      isPreschoolFFE,
      isPreschool
    };
  },
  LOGIN: (state, action) => {
    const {
      user,
      role,
      isKrismar,
      isColombiaUser,
      isColombiaSierraUser,
      isImex,
      isPreschool,
      personLevel,
      personGrade,
      personLevelId,
      personGradeId
    } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      role,
      isKrismar,
      isColombiaUser,
      isColombiaSierraUser,
      isImex,
      isPreschool,
      personLevel,
      personGrade,
      personLevelId,
      personGradeId
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken) {
          setSession(accessToken, true);
          const uuid = JWTDecode(JSON.stringify(accessToken));
          const response = await axiosInstance.get(`/users/uuid/${uuid.uuid}`);
          const role = await getRole(response);
          let personLevel = null;
          let personGrade = null;
          let personLevelId = null;
          let personGradeId = null;
          let isPreschoolFFE = null;
          let isPreschool = null;
          // if (role === 'isTeacher' || role === 'isStudent') {
          if (role === 'isStudent') {
            const studentData = await axiosInstance.get(`/students-v2/person/${response.data.personId}`);
            personLevel = studentData.data.academicGrade.academicLevel.code;
            personGrade = studentData.data.academicGrade.code;
            personLevelId = studentData.data.academicGrade.academicLevel.id;
            personGradeId = studentData.data.academicGrade.id;
            isPreschoolFFE = await checkIsPreschoolFFE(studentData.data);
            isPreschool = await checkIsPreschool(studentData.data);
          } else if (role === 'isTeacher') {
            const teacherData = await axiosInstance.get(
              `/v2/school-personnel/person/${response.data.personId}?schoolId=${response.data.roles[0].schoolId}&academicYearId=28`
            );
            isPreschoolFFE = await checkIsPreschoolFFE(teacherData.data);
            isPreschool = await checkIsPreschool(teacherData.data);
          }
          const isKrismar = await checkIsKrismar(response);
          const isColombiaUser = await checkIsColombia(response, role);
          const isColombiaSierraUser = await checkIsColombiaSierra(response, role);
          const isImex = await checkIsImex(response);
          const user = response.data;
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
              role,
              isKrismar,
              isColombiaUser,
              isColombiaSierraUser,
              isImex,
              personLevel,
              personGrade,
              personLevelId,
              personGradeId,
              isPreschoolFFE,
              isPreschool
            }
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              role: null,
              isKrismar: null,
              isColombiaUser: null,
              isColombiaSierraUser: null,
              isImex: null,
              isPreschoolFFE: null,
              isPreschool: null
            }
          });
        }
      } catch (err) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            role: null,
            isKrismar: null,
            isColombiaSierraUser: null,
            isImex: null,
            isColombiaUser: null,
            isPreschoolFFE: null,
            isPreschool: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axiosInstance.post(`users/authenticate`, {
        username,
        password
      });
      const { jwt } = response.data;
      const uuid = JWTDecode(JSON.stringify(jwt));
      const responseUser = await axiosInstance.get(`/users/uuid/${uuid.uuid}`);
      const user = responseUser.data;
      const role = await getRole(responseUser);
      let personLevel = null;
      let personGrade = null;
      let personLevelId = null;
      let personGradeId = null;
      let isPreschoolFFE = null;
      let isPreschool = null;
      if (role === 'isStudent') {
        const studentData = await axiosInstance.get(`/students-v2/person/${responseUser.data.personId}`);
        personLevel = studentData.data.academicGrade.academicLevel.code;
        personGrade = studentData.data.academicGrade.code;
        personLevelId = studentData.data.academicGrade.academicLevel.id;
        personGradeId = studentData.data.academicGrade.id;
      } else if (role === 'isTeacher') {
        const { personId, roles, academicYear } = responseUser.data;
        if (roles && roles.length > 0 && roles[0].schoolId && academicYear && academicYear.id) {
          const [{ schoolId }] = roles;
          const academicYearId = academicYear.id;
          const teacherData = await axiosInstance.get(
            `/v2/school-personnel/person/${personId}?schoolId=${schoolId}&academicYearId=${academicYearId}`
          );
          isPreschoolFFE = await checkIsPreschoolFFE(teacherData.data);
          isPreschool = await checkIsPreschool(teacherData.data);
        } else {
          console.error('No se encontro schoolId o academicYearId');
          console.log('SchoolId: ', roles[0].schoolId);
          console.log('AcademicYearId: ', academicYear.id);
        }
      }
      const isKrismar = await checkIsKrismar(responseUser);
      const isColombiaUser = await checkIsColombia(responseUser, role);
      const isColombiaSierraUser = await checkIsColombiaSierra(responseUser, role);
      const isImex = await checkIsImex(responseUser);
      dispatch({
        type: 'LOGIN',
        payload: {
          role,
          user,
          isColombiaUser,
          isColombiaSierraUser,
          isImex,
          isPreschool,
          isKrismar,
          personLevel,
          personGrade,
          personLevelId,
          personGradeId
        }
      });
      setSession(jwt, false);
    } catch (error) {
      console.log('Error on login');
      console.log(error);
    }
  };

  const register = async (username, password, firstName, lastName) => {
    const response = await axiosInstance.post('/api/account/register', {
      username,
      password,
      firstName,
      lastName
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);
    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const resetPassword = async (user) => {
    try {
      const response = await axiosInstance.post('/users/request-password', {
        username: user
      });
      return response;
    } catch (error) {
      alert('error');
      return error;
    }
  };

  const updateProfile = () => {};

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
